import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

function ScrollToTop() {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);
  return null;
}

export default function AboutPage() {
  return (
    <>
      <ScrollToTop />
      <Navbar />

      <main className="pt-16">
        {/* Breadcrumb */}
        <section className="bg-background-100 border-b border-background-200/70">
          <div className="max-w-5xl mx-auto px-4 md:px-6 py-6">
            <nav className="flex items-center gap-2 text-xs text-foreground-500 flex-wrap" aria-label="面包屑导航">
              <Link to="/" className="hover:text-primary-500 transition-colors cursor-pointer">首页</Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <span className="text-foreground-800 font-medium">关于我们</span>
            </nav>
            <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mt-3">
              关于麻花影视
            </h1>
            <p className="text-sm text-foreground-600 mt-1">
              聚合飘花电影网、555电影、8090电影网等优质资源，打造一站式高清影视平台
            </p>
          </div>
        </section>

        {/* Brand introduction */}
        <section className="py-14 md:py-20 bg-background-50">
          <div className="max-w-5xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
              <div>
                <h3 className="text-sm font-semibold text-primary-500 uppercase tracking-wider mb-2">
                  <i className="ri-building-line mr-1.5"></i>
                  品牌故事
                </h3>
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-6">
                  让每个人都能免费看好电影
                </h2>
                <div className="space-y-4 text-sm text-foreground-700 leading-relaxed">
                  <p>
                    麻花影视成立于2024年，致力于为用户提供最全面、最高清的电影、电视剧、综艺节目和动漫资源。我们深知用户对优质观影体验的渴望，因此聚合了飘花电影网、555电影、8090电影网等多家优质影视平台的资源，打造了一个片库全面、更新及时、播放流畅的一站式影视平台。
                  </p>
                  <p>
                    经过两年多的发展，麻花影视已经成为中文互联网上最受欢迎的免费影视平台之一，日均访客量突破500万。我们拥有超过36,000部影视资源，覆盖电影、电视剧、综艺、动漫四大品类，每天更新200+部最新内容。
                  </p>
                  <p>
                    我们的使命是让每个人都能免费欣赏到高质量的电影内容。无论你身处何地，使用什么设备，都能在麻花影视找到想看的影视作品，享受流畅、高清的观看体验。
                  </p>
                </div>
              </div>

              <div className="rounded-xl overflow-hidden h-72 md:h-80">
                <img
                  src="https://readdy.ai/api/search-image?query=Modern%20minimalist%20home%20cinema%20setup%20with%20large%20screen%20displaying%20movie%2C%20warm%20ambient%20lighting%20with%20golden%20and%20amber%20tones%2C%20cozy%20atmosphere%2C%20dark%20rich%20background%2C%20professional%20interior%20photography%2C%20clean%20composition&width=800&height=600&seq=about-brand&orientation=landscape"
                  alt="麻花影视品牌形象"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Advantages */}
        <section className="py-14 md:py-20 bg-background-100">
          <div className="max-w-5xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h3 className="text-sm font-semibold text-accent-500 uppercase tracking-wider mb-2">
                <i className="ri-medal-line mr-1.5"></i>
                平台优势
              </h3>
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">
                为什么选择麻花影视？
              </h2>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {[
                {
                  icon: 'ri-database-2-line',
                  title: '片库全面',
                  desc: '聚合飘花电影网、555电影、8090电影网等平台资源，超过36,000部影视作品任你选择。从院线大片到经典老片，从热播剧集到冷门佳作，应有尽有。',
                },
                {
                  icon: 'ri-speed-up-line',
                  title: '极速播放',
                  desc: '采用先进的CDN加速技术，多线路智能切换，确保高清视频流畅播放。告别卡顿和缓冲，享受丝滑观影体验。',
                },
                {
                  icon: 'ri-timer-line',
                  title: '每日更新',
                  desc: '片库每日更新200+部，第一时间同步各大平台最新资源。无论是刚上映的电影还是最新开播的剧集，你都能在第一时间观看。',
                },
                {
                  icon: 'ri-4k-line',
                  title: '高清画质',
                  desc: '支持720P至4K多种画质选择，部分影片提供HDR和杜比视界版本。给你影院级的视觉享受。',
                },
                {
                  icon: 'ri-smartphone-line',
                  title: '多端适配',
                  desc: '全面适配手机、平板和电脑，界面自动适配不同屏幕。随时随地，想看在就看。',
                },
                {
                  icon: 'ri-shield-check-line',
                  title: '安全免费',
                  desc: '完全免费，无需注册，无需付费。纯净观影体验，无广告干扰，保护你的隐私安全。',
                },
              ].map((item, idx) => (
                <div key={idx} className="p-6 rounded-xl bg-background-50 border border-background-200/70 hover:border-primary-200 transition-all duration-200">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 mb-4">
                    <i className={`${item.icon} text-lg`}></i>
                  </div>
                  <h3 className="text-base font-heading font-bold text-foreground-950 mb-2">{item.title}</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Reputation / Stats */}
        <section className="py-14 md:py-20 bg-background-50">
          <div className="max-w-5xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h3 className="text-sm font-semibold text-secondary-500 uppercase tracking-wider mb-2">
                <i className="ri-heart-line mr-1.5"></i>
                用户口碑
              </h3>
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">
                500万+用户的选择
              </h2>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-5 mb-10">
              {[
                { value: '36,000+', label: '影视资源' },
                { value: '500万+', label: '日均访客' },
                { value: '99.9%', label: '播放成功率' },
                { value: '4.8', label: '用户评分' },
              ].map((stat) => (
                <div key={stat.label} className="text-center p-5 rounded-xl bg-background-100 border border-background-200/70">
                  <div className="text-2xl md:text-3xl font-bold text-primary-500 font-heading">{stat.value}</div>
                  <div className="text-sm text-foreground-500 mt-1">{stat.label}</div>
                </div>
              ))}
            </div>

            {/* Testimonials */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {[
                { text: '麻花影视的片库真的太全了，我想看的每一部电影都能找到。而且画质超清晰，播放一点都不卡！', author: '影迷小王', role: '资深电影爱好者' },
                { text: '用了麻花影视之后，再也不用到处找资源了。每天更新很快，新剧上线第一天就能看，太方便了！', author: '追剧达人', role: '电视剧重度用户' },
                { text: '界面简洁干净，没有乱七八糟的广告。手机和电脑都能用，体验非常好。强烈推荐给身边的朋友。', author: '李先生', role: '麻花影视忠实用户' },
              ].map((item, idx) => (
                <div key={idx} className="p-6 rounded-xl bg-background-100 border border-background-200/70">
                  <div className="flex items-center gap-1 mb-3">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <i key={star} className="ri-star-fill text-accent-500 text-sm"></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                    &ldquo;{item.text}&rdquo;
                  </p>
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center">
                      <span className="text-xs font-bold text-primary-600">{item.author[0]}</span>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-900">{item.author}</p>
                      <p className="text-xs text-foreground-500">{item.role}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact info */}
        <section className="py-14 md:py-20 bg-background-100">
          <div className="max-w-5xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h3 className="text-sm font-semibold text-primary-500 uppercase tracking-wider mb-2">
                <i className="ri-mail-line mr-1.5"></i>
                联系我们
              </h3>
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">
                期待你的声音
              </h2>
              <p className="mt-2 text-sm text-foreground-600 max-w-lg mx-auto">
                如果你有任何建议、意见或合作意向，欢迎通过以下方式联系我们
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 max-w-3xl mx-auto">
              {[
                { icon: 'ri-mail-line', title: '电子邮箱', content: 'contact@mahua.tv', color: 'bg-primary-100 text-primary-600' },
                { icon: 'ri-wechat-line', title: '微信公众号', content: '麻花影视', color: 'bg-accent-100 text-accent-600' },
                { icon: 'ri-question-line', title: '帮助中心', content: '查看常见问题', color: 'bg-secondary-100 text-secondary-600' },
              ].map((item, idx) => (
                <div key={idx} className="p-6 rounded-xl bg-background-50 border border-background-200/70 text-center">
                  <div className={`w-12 h-12 mx-auto flex items-center justify-center rounded-full ${item.color} mb-4`}>
                    <i className={`${item.icon} text-xl`}></i>
                  </div>
                  <h3 className="text-sm font-heading font-bold text-foreground-950 mb-1">{item.title}</h3>
                  <p className="text-sm text-foreground-600">{item.content}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}